import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { WorktimeEmpty } from '../icons';
import NoData from '../dashboard/noData';
import { useTranslation } from 'react-i18next';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { IFilterOption } from '../../interface/transaction';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchWorkTimeList } from '../../hooks/useFetchWorkTimeList';
import { useParams, useSearchParams } from 'react-router-dom';
import { IWorkTime, WorkTimeFilters } from '../../interface/workTimes';
import WorkTimeCard from './workTimeCard';
import { Spin } from '../Ant';
import { useWorkTime } from '../../context/WorkTimeContext';
import { ITask } from '../../interface/tasks';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import { useUser } from '../../context/UserContext';
import SkeletonGroupedCardList from '../common/skeleton/skeletonGroupedCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import CollapsibleDateGroup from '../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../hooks/useCollapsedDateGroups';
import { useFetchAllPages } from '../../hooks/useFetchAllPages';
interface ITaskFormated {
  displayKey: string;
  date: Date;
  duration: number;
  items: IWorkTime[];
}
const WorkTimes = ({ task }: { task?: ITask }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { updateWorkTime } = useWorkTime();
  const { user } = useUser();
  const { push } = useNavigationStack();
  const queryClient = useQueryClient();
  const todayDate = dayjs().format('YYYY-MM-DD');
  const workTimeFilterOptions: IFilterOption[] = [
    { label: t('worktime:filter.myOwn'), value: WorkTimeFilters.MY },
    { label: t('worktime:filter.otherAuthors'), value: WorkTimeFilters.OTHER },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('worktimeSearch') || '';
  const initialFilterValue = searchParams.get('worktimeFilter') || '';
  const initialFilterValues = initialFilterValue ? initialFilterValue.split(',') : [];
  const initialSelectedFilter = workTimeFilterOptions.filter((opt) =>
    initialFilterValues.includes(opt.value),
  );
  const [selectedFilter, setSelectedFilter] = useState<IFilterOption[]>(initialSelectedFilter);
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const handleAuthorFilter = () => {
    if (!selectedFilter) {
      return undefined;
    }

    const myFilterApplied = selectedFilter.some((val) => val.value.includes(WorkTimeFilters.MY));
    const otherFilterApplied = selectedFilter.some((val) =>
      val.value.includes(WorkTimeFilters.OTHER),
    );

    if (myFilterApplied && otherFilterApplied) {
      return undefined;
    } else if (myFilterApplied) {
      return user?.id;
    } else if (otherFilterApplied) {
      return `$not:${user?.id}`;
    }
    return undefined;
  };

  const {
    data: workTimesList,
    isLoading: isWorkTimeLoading,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useFetchWorkTimeList({
    pageSize: 50,
    search: searchValue,
    extraFilters: {
      activityId: `$eq:${id}`,
      workTimeUserId: handleAuthorFilter(),
    },
    relations: ['transaction', 'workTimeType'],
  });

  const groupedMap = (workTimesList ?? []).reduce(
    (acc, item): Record<string, { displayKey: string; date: Date; items: IWorkTime }> => {
      const itemDayjs = dayjs(item.startTime);
      const itemDateKey = itemDayjs.format('YYYY-MM-DD');
      const displayKey = itemDateKey === todayDate ? 'Today' : itemDayjs.format('MMM DD, YYYY');

      acc[displayKey] ??= {
        displayKey,
        date: itemDayjs.toDate(), // for sorting
        duration: 0,
        items: [],
      };
      acc[displayKey].duration += item.duration;

      acc[displayKey].items.push(item);
      return acc;
    },
    {} as ITaskFormated,
  ) as ITaskFormated[];

  // Convert to array and sort by actual date
  const sortedGrouped = Object.values(groupedMap).sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );
  const { isCollapsed, toggle, getToggleAllControl } = useCollapsedDateGroups(
    `${DateGroupList.workTimes}:${id}`,
  );
  useFetchAllPages({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage });
  useEffect(() => {
    setSearchParams({
      worktimeSearch: searchValue,
      worktimeFilter: selectedFilter.map((f) => f.value).join(','),
    });
  }, [searchValue, selectedFilter]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };

  const handleAddWorkTime = () => {
    updateWorkTime({
      activity: task,
    } as IWorkTime);
    push(ROUTES.ADD_EDIT_WORK_TIME);
  };
  const handleWorkTimeDetail = (workTimeId: string) => {
    push(`${ROUTES.WORK_TIME_DETAIL}/${workTimeId}`);
  };
  return (
    <CollapsibleListLayout
      testId="work-times-listing"
      contentGap="gutter"
      header={
        <SearchInputWithFilters
          dateGroupToggle={getToggleAllControl(
            sortedGrouped.map((group) => toDateGroupKey(group.date)),
          )}
          searchTitle={t('worktime:search')}
          filterTitle={t('worktime:filter.label')}
          checkboxOptions={workTimeFilterOptions}
          searchValue={searchValue}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['worktime-list'] });
            return refetch();
          }}
          clearFilter={(val?: string) => {
            if (val) {
              setSelectedFilter((prev) => prev.filter((item) => item.value !== val));
            } else {
              setSelectedFilter([]);
            }
          }}
          selectedFilter={selectedFilter}
          setSelectedCheckboxFilter={(val: IFilterOption[]) => {
            setSelectedFilter(val);
          }}
          handleAdd={handleAddWorkTime}
          tooltipTextForAdd={t('worktime:addWorkTime')}
        />
      }
    >
      {sortedGrouped && Object.entries(sortedGrouped).length > 0 && (
        <>
          {sortedGrouped.map((val: ITaskFormated) => {
            const groupKey = toDateGroupKey(val.date);
            return (
              <CollapsibleDateGroup
                key={groupKey}
                data-testid={`worktime-date-bar-${val.displayKey}`}
                title={val.displayKey}
                itemCount={t('worktime:workTimeCount', { count: val.items.length })}
                total={`${val.duration} ${t('common:h')}`}
                collapsed={isCollapsed(groupKey)}
                onToggle={() => toggle(groupKey)}
              >
                {val.items.map((workTime: IWorkTime) => (
                  <WorkTimeCard
                    data-testid={`worktime-card`}
                    key={workTime?.id}
                    workTime={workTime}
                    onPress={() => handleWorkTimeDetail(workTime?.id)}
                  />
                ))}
              </CollapsibleDateGroup>
            );
          })}
        </>
      )}
      {(!workTimesList || (Object.entries(workTimesList).length === 0 && !isWorkTimeLoading)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData header={t('worktime:noWorkTimes')} subHeader="" icon={<WorktimeEmpty />} />
        </div>
      )}
      {isWorkTimeLoading ? <SkeletonGroupedCardList rows={[1, 2, 1]} /> : renderLoader()}
    </CollapsibleListLayout>
  );
};

export default WorkTimes;
